import React, { useState, useEffect, useLayoutEffect, createContext, useContext } from "react";
import {
  THEME_STORAGE_KEY,
  getThemePreference,
  saveThemePreference,
  systemPrefersDark,
  applyThemeToDocument,
} from "./themeUtils";
import { ConfigProvider, theme } from "antd";

// Create theme context
const ThemeContext = createContext({
  isDarkMode: false,
  toggleTheme: () => {},
  setTheme: () => {},
  themeMode: "system",
});

export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  // The preference is three-state: automatic (system), light, or dark.
  // 'theme' is the existing storage key, so previous manual choices still work.
  const [themeMode, setThemeMode] = useState(getThemePreference);
  const [systemIsDark, setSystemIsDark] = useState(systemPrefersDark);
  const isDarkMode = themeMode === "system" ? systemIsDark : themeMode === "dark";

  const setTheme = (nextMode) => {
    if (!["system", "light", "dark"].includes(nextMode)) return;
    saveThemePreference(nextMode);
    setThemeMode(nextMode);
  };

  // A quick toggle always chooses a manual mode; use "System" to resume
  // following the device/browser preference automatically.
  const toggleTheme = () => setTheme(isDarkMode ? "light" : "dark");

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const query = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!query) return undefined;

    const handleSystemChange = (event) => setSystemIsDark(event.matches);
    // Older Safari versions implement only addListener/removeListener.
    if (query.addEventListener) query.addEventListener("change", handleSystemChange);
    else query.addListener?.(handleSystemChange);

    return () => {
      if (query.removeEventListener) query.removeEventListener("change", handleSystemChange);
      else query.removeListener?.(handleSystemChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleStorageChange = (event) => {
      if (event.key === THEME_STORAGE_KEY || event.key === null) {
        setThemeMode(getThemePreference());
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Apply the mode to Tailwind, native controls, mobile browser chrome, and
  // the whole document before paint when possible.
  useLayoutEffect(() => {
    applyThemeToDocument(isDarkMode);
  }, [isDarkMode]);

  // Complete theme override to remove all blue hover
  const getThemeConfig = (isDark) => ({
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorBgBase: isDark ? "#111827" : "#ffffff",
      // Primary colors - only for active/selected states
      colorPrimary: "#22c55e",
      colorPrimaryHover: "#22c55e",
      colorPrimaryActive: "#15803d",
      colorPrimaryBg: isDark ? "#1a2e1a" : "#f0fdf4",
      colorPrimaryBgHover: isDark ? "#1f3b1f" : "#dcfce7",
      
      // Link colors - remove blue completely
      colorLink: "#22c55e",
      colorLinkHover: "#16a34a",
      colorLinkActive: "#15803d",
      
      // Text colors
      colorText: isDark ? "#f9fafb" : "#1f2937",
      colorTextSecondary: isDark ? "#9ca3af" : "#6b7280",
      colorTextTertiary: isDark ? "#6b7280" : "#9ca3af",
      colorTextQuaternary: isDark ? "#4b5563" : "#d1d5db",
      
      // Backgrounds
      colorBgContainer: isDark ? "#1f2937" : "#ffffff",
      colorBgElevated: isDark ? "#374151" : "#ffffff",
      colorBgLayout: isDark ? "#111827" : "#f5f5f5",
      
      // Borders
      colorBorder: isDark ? "#4b5563" : "#e5e7eb",
      colorBorderSecondary: isDark ? "#374151" : "#f3f4f6",
      
      // Control states - NO BLUE
      colorBgTextHover: isDark ? "#374151" : "#f3f4f6",
      colorBgTextActive: isDark ? "#4b5563" : "#e5e7eb",
      
      // Other
      borderRadius: 6,
      borderRadiusLG: 8,
      borderRadiusSM: 4,
      colorSuccess: "#22c55e",
      colorWarning: "#f59e0b",
      colorError: "#ef4444",
      colorInfo: "#3b82f6",
      fontSize: 14,
    },
    components: {
      Button: {
        // Default button
        defaultBg: isDark ? "#374151" : "#ffffff",
        defaultColor: isDark ? "#f9fafb" : "#1f2937",
        defaultBorderColor: isDark ? "#4b5563" : "#e5e7eb",
        defaultHoverBg: isDark ? "#4b5563" : "#f3f4f6",
        defaultHoverColor: isDark ? "#f9fafb" : "#1f2937",
        defaultHoverBorderColor: isDark ? "#6b7280" : "#d1d5db",
        defaultActiveBg: isDark ? "#4b5563" : "#e5e7eb",
        defaultActiveColor: isDark ? "#f9fafb" : "#1f2937",
        defaultActiveBorderColor: isDark ? "#6b7280" : "#d1d5db",
        
        // Primary button
        colorPrimary: "#22c55e",
        colorPrimaryHover: "#16a34a",
        colorPrimaryActive: "#15803d",
        
        // Text button
        textHoverBg: isDark ? "#374151" : "#f3f4f6",
        
        // Link button
        linkHoverBg: "transparent",
        
        borderRadius: 6,
        controlHeight: 36,
      },
      Input: {
        colorBgContainer: isDark ? "#374151" : "#ffffff",
        colorText: isDark ? "#f9fafb" : "#1f2937",
        colorBorder: isDark ? "#4b5563" : "#e5e7eb",
        colorPlaceholder: isDark ? "#6b7280" : "#9ca3af",
        activeBorderColor: "#22c55e",
        hoverBorderColor: isDark ? "#6b7280" : "#9ca3af",
        activeShadow: "0 0 0 2px rgba(34, 197, 94, 0.1)",
      },
      Select: {
        colorBgContainer: isDark ? "#374151" : "#ffffff",
        colorText: isDark ? "#f9fafb" : "#1f2937",
        colorBorder: isDark ? "#4b5563" : "#e5e7eb",
        colorPrimary: "#22c55e",
        colorPrimaryHover: "#16a34a",
        optionSelectedColor: isDark ? "#f9fafb" : "#1f2937",
        optionSelectedBg: isDark ? "#1a2e1a" : "#f0fdf4",
        optionActiveBg: isDark ? "#374151" : "#f3f4f6",
      },
      Dropdown: {
        colorBgElevated: isDark ? "#374151" : "#ffffff",
        colorText: isDark ? "#f9fafb" : "#1f2937",
        controlItemBgHover: isDark ? "#4b5563" : "#f3f4f6",
      },
      Menu: {
        colorItemBg: "transparent",
        colorItemText: isDark ? "#f9fafb" : "#1f2937",
        colorItemBgHover: isDark ? "#374151" : "#f3f4f6",
        colorItemTextHover: isDark ? "#f9fafb" : "#1f2937",
        colorItemBgSelected: isDark ? "#1a2e1a" : "#f0fdf4",
        colorItemTextSelected: "#22c55e",
        colorItemBgActive: isDark ? "#374151" : "#f3f4f6",
        colorItemTextActive: isDark ? "#f9fafb" : "#1f2937",
      },
      Tabs: {
        colorText: isDark ? "#f9fafb" : "#1f2937",
        colorPrimary: "#22c55e",
        colorPrimaryHover: "#16a34a",
        inkBarColor: "#22c55e",
        itemHoverColor: isDark ? "#d1d5db" : "#4b5563",
        itemSelectedColor: "#22c55e",
      },
      Table: {
        colorBgContainer: isDark ? "#1f2937" : "#ffffff",
        colorText: isDark ? "#f9fafb" : "#1f2937",
        colorTextHeading: isDark ? "#f9fafb" : "#1f2937",
        borderColor: isDark ? "#4b5563" : "#e5e7eb",
        rowHoverBg: isDark ? "#374151" : "#f3f4f6",
        headerBg: isDark ? "#374151" : "#f9fafb",
        headerColor: isDark ? "#f9fafb" : "#1f2937",
      },
      Modal: {
        colorBgElevated: isDark ? "#374151" : "#ffffff",
        colorText: isDark ? "#f9fafb" : "#1f2937",
        colorTextSecondary: isDark ? "#9ca3af" : "#6b7280",
      },
      Pagination: {
        colorBgContainer: isDark ? "#1f2937" : "#ffffff",
        colorText: isDark ? "#f9fafb" : "#1f2937",
        colorPrimary: "#22c55e",
        colorPrimaryHover: "#16a34a",
        colorBorder: isDark ? "#4b5563" : "#e5e7eb",
      },
      Breadcrumb: {
        colorText: isDark ? "#9ca3af" : "#6b7280",
        linkColor: isDark ? "#f9fafb" : "#1f2937",
        linkHoverColor: "#22c55e",
        lastItemColor: isDark ? "#f9fafb" : "#1f2937",
      },
      Checkbox: {
        colorPrimary: "#22c55e",
        colorPrimaryHover: "#16a34a",
      },
      Radio: {
        colorPrimary: "#22c55e",
        colorPrimaryHover: "#16a34a",
      },
      Switch: {
        colorPrimary: "#22c55e",
        colorPrimaryHover: "#16a34a",
      },
      Progress: {
        colorPrimary: "#22c55e",
      },
      Spin: {
        colorPrimary: "#22c55e",
      },
      Tag: {
        colorBgContainer: isDark ? "#374151" : "#f5f5f5",
        colorText: isDark ? "#f9fafb" : "#1f2937",
      },
      Alert: {
        colorPrimary: "#22c55e",
        colorPrimaryBg: isDark ? "#1a2e1a" : "#f0fdf4",
        colorPrimaryBorder: isDark ? "#2d4a2d" : "#bbf7d0",
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, setTheme, themeMode }}>
      <ConfigProvider theme={getThemeConfig(isDarkMode)}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;